// Auth-boundary decision logic for the router beforeEach guard.
//
// Extracted as a pure, dependency-injected function so it can be exercised
// deterministically without Vue, vue-router, or a DOM. Two distinct gates,
// applied in order of strictness:
//
//   1. IRL2-2901 — cross-campaign token-bleed. A token stamped for a
//      DIFFERENT campaign than the one in the URL is always a bleed; bounce
//      to Register before any view renders. This covers "Custom" routes
//      (the how-to-play page is a Custom page), which the original 2876 gate
//      wrongly exempted, causing a flash of how-to-play before the login
//      screen on a 2nd campaign.
//
//   2. IRL2-2876 — no-token gate. An authenticated route with no token at
//      all bounces to Register. Pre-auth pages (incl. "Custom" for anonymous
//      T&C / landing) stay exempt so they still render without a token
//      (ask-africa T&C incident, 2026-04-24).

export interface DecodedToken {
	["custom:campaign_id"]?: string;
}

export interface AuthBoundaryInput {
	routeName: string | null | undefined;
	/** to.params.campaign — the campaign in the URL, the source of truth. */
	navCampaignId: string | undefined;
	/** campaignStore().getItem?.id — fallback for the redirect param only. */
	activeCampaignId: string | undefined;
	accessToken: string | null;
	/** Injected jwtDecode. May throw on a malformed token; callers tolerate that. */
	decodeToken: (token: string) => DecodedToken | null | undefined;
}

export interface AuthBoundaryRedirect {
	name: "Register";
	params: { campaign: string };
}

// Routes where a stale/foreign token must NOT trigger a bounce: the dedicated
// auth-entry and system pages. A user holding campaign A's token may
// legitimately be sitting on B's Register/SignIn/paused screens while
// switching. NB: "Custom" is deliberately ABSENT here (IRL2-2901) — a Custom
// page (how-to-play) held under a foreign-campaign token IS the bleed.
export const MISMATCH_EXEMPT_ROUTES = new Set<string>([
	"Register",
	"SignIn",
	"Enter Pin",
	"CampaignPaused",
	"OutOfPlays",
	"DeviceDetection",
	"Error",
]);

// Routes reachable with NO token at all (anonymous). Mirrors
// httpClient.ts publicRouteNames. Includes "Custom" so anonymous pre-auth
// custom pages (T&Cs / landing) still render without a token.
export const NO_TOKEN_EXEMPT_ROUTES = new Set<string>([
	"Register",
	"SignIn",
	"Enter Pin",
	"CampaignPaused",
	"OutOfPlays",
	"DeviceDetection",
	"Error",
	"Custom",
]);

export function evaluateAuthBoundary(input: AuthBoundaryInput): AuthBoundaryRedirect | null {
	const { routeName, navCampaignId, activeCampaignId, accessToken, decodeToken } = input;
	if (!routeName) {
		return null;
	}

	// 1. Cross-campaign token-bleed guard — IRL2-2901.
	if (!MISMATCH_EXEMPT_ROUTES.has(routeName) && accessToken && navCampaignId) {
		let tokenCampaignId: string | undefined;
		try {
			tokenCampaignId = decodeToken(accessToken)?.["custom:campaign_id"];
		} catch {
			// Malformed token — leave cleanup to the authStore guard /
			// httpClient interceptor; do not bounce on a token we cannot read.
			tokenCampaignId = undefined;
		}
		if (tokenCampaignId && tokenCampaignId !== navCampaignId) {
			return { name: "Register", params: { campaign: navCampaignId } };
		}
	}

	// 2. No-token gate for authenticated routes — IRL2-2876.
	if (!NO_TOKEN_EXEMPT_ROUTES.has(routeName) && !accessToken) {
		const campaignParam = navCampaignId || activeCampaignId;
		if (campaignParam) {
			return { name: "Register", params: { campaign: campaignParam } };
		}
	}

	return null;
}
